import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Info } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Divider } from "../components/Divider";
import { PillButton } from "../components/PillButton";
import { API } from "../lib/constants";

const EXAMPLES = [
  "I live in my dream home by the beach in LA.",
  "My bank account grows every day.",
  "I am healthy, strong, and full of energy.",
  "I am confident and at peace with myself.",
  "My work is in high demand, and new clients keep coming.",
];

const inputBase =
  "w-full rounded-xl border border-[rgba(43,38,32,0.15)] bg-cream px-5 py-3.5 text-base text-ink placeholder:text-ink-soft/60 outline-none transition-[border-color,box-shadow] duration-300 focus:border-terracotta focus:shadow-[0_0_0_4px_rgba(201,123,74,0.12)]";

const CreateMyAudio = () => {
  const [form, setForm] = useState({
    email: "",
    name: "",
    affirmations: "",
    length: "60",
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("paid") === "true") {
      localStorage.setItem("uf_paid", "true");
    }
    if (sessionId) {
      // Confirm the payment status (also reconciles the backend record).
      axios.get(`${API}/payments/status/${sessionId}`).catch(() => {});
      localStorage.setItem("uf_current_session", sessionId);
    }
    // "Submitted" is tied to a specific paid session, so every NEW payment shows the form again.
    const activeSession = sessionId || localStorage.getItem("uf_current_session");
    if (activeSession && localStorage.getItem("uf_submitted_session") === activeSession) {
      setSubmitted(true);
    }
    const prefEmail = params.get("email");
    if (prefEmail) setForm((f) => ({ ...f, email: prefEmail }));
    window.scrollTo(0, 0);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/submissions`, {
        email: form.email,
        name: form.name,
        affirmations: form.affirmations,
        length: form.length,
      });
      localStorage.setItem("uf_submitted", "true");
      const sid =
        new URLSearchParams(window.location.search).get("session_id") ||
        localStorage.getItem("uf_current_session") ||
        "no-session";
      localStorage.setItem("uf_submitted_session", sid);
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong sending your affirmations. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-cream">
      <Navbar />
      <main className="px-6 pb-24 pt-36 lg:px-12">
        <div className="mx-auto max-w-[600px]">
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="thanks"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col items-center rounded-[20px] border border-[rgba(43,38,32,0.1)] bg-cream2 px-8 py-16 text-center"
                data-testid="submission-thankyou"
              >
                <span className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-terracotta text-white">
                  <Check strokeWidth={1.75} size={30} />
                </span>
                <h1 className="max-w-md font-serif text-3xl font-medium leading-tight text-ink sm:text-4xl">
                  Thank you! We've received your affirmations.
                </h1>
                <p className="mt-4 max-w-md text-base leading-relaxed text-ink-soft">
                  Your personalized audio will be in your inbox within 48 hours.
                </p>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-ink-soft">
                  Questions in the meantime? Reach us at{" "}
                  <a
                    href="mailto:universaflow@outlook.com"
                    className="text-terracotta underline underline-offset-2"
                  >
                    universaflow@outlook.com
                  </a>
                </p>
              </motion.div>
            ) : (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Reveal>
                  <p
                    className="text-xs font-medium uppercase tracking-[0.24em] text-terracotta"
                    data-testid="post-payment-eyebrow"
                  >
                    Payment received — thank you
                  </p>
                  <h1 className="mt-5 font-serif text-4xl font-medium leading-[1.08] text-ink sm:text-5xl">
                    Now, let's create new beliefs in your subconscious.
                  </h1>
                  <p className="mt-5 text-base leading-relaxed text-ink-soft sm:text-lg">
                    Write down what you wish to manifest in your life right now. Be as specific as
                    you'd like — this is entirely about your own growth and your own journey.
                  </p>
                </Reveal>

                {/* Highlighted note */}
                <Reveal delay={0.08}>
                  <div
                    className="mt-8 flex gap-3 rounded-2xl border-l-4 border-terracotta bg-terracotta/10 px-5 py-4"
                    data-testid="note-box"
                  >
                    <Info size={20} strokeWidth={1.5} className="mt-0.5 shrink-0 text-terracotta" />
                    <p className="text-sm leading-relaxed text-ink">
                      A quick note before you write: we can only work with intentions about your own
                      life and growth — we're not able to include affirmations meant to influence
                      another person's feelings, choices, or free will.
                    </p>
                  </div>
                </Reveal>

                {/* Examples */}
                <Reveal delay={0.12}>
                  <div className="mt-6 rounded-2xl bg-cream2 px-6 py-6" data-testid="examples-box">
                    <p className="text-sm font-medium text-ink">
                      Need a starting point? Here are a few examples:
                    </p>
                    <ul className="mt-4 space-y-3">
                      {EXAMPLES.map((ex) => (
                        <li key={ex} className="flex gap-3">
                          <span className="mt-1 block h-4 w-[3px] shrink-0 rounded-full bg-terracotta" />
                          <span className="font-serif text-base italic leading-relaxed text-ink-soft">
                            “{ex}”
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Divider className="my-5 !justify-start" />
                    <p className="text-sm leading-relaxed text-ink-soft">
                      We recommend limiting your audio to up to 20 affirmations so each one can be
                      repeated multiple times, enhancing focus and consistency throughout your
                      practice.
                    </p>
                  </div>
                </Reveal>

                {/* Form */}
                <Reveal delay={0.16}>
                  <form onSubmit={handleSubmit} className="mt-10 grid gap-6" data-testid="affirmations-form">
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-ink">
                        Email you used to pay
                      </span>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@email.com"
                        className={inputBase}
                        data-testid="field-email"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-ink">Your Name</span>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Your name"
                        className={inputBase}
                        data-testid="field-name"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-ink">
                        Your affirmations (up to 20, one per line)
                      </span>
                      <textarea
                        required
                        rows={9}
                        value={form.affirmations}
                        onChange={update("affirmations")}
                        placeholder={"I want...\nI am...\nI trust..."}
                        className={`${inputBase} resize-none leading-relaxed`}
                        data-testid="field-affirmations"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-ink">
                        Preferred length
                      </span>
                      <select
                        value={form.length}
                        onChange={update("length")}
                        className={inputBase}
                        data-testid="field-length"
                      >
                        <option value="30">30 minutes</option>
                        <option value="60">60 minutes</option>
                      </select>
                    </label>

                    {error && (
                      <p className="text-sm text-red-700" data-testid="form-error">
                        {error}
                      </p>
                    )}

                    <div>
                      <PillButton
                        type="submit"
                        variant="terracotta"
                        disabled={sending}
                        className="w-full sm:w-auto disabled:opacity-70"
                        data-testid="submit-affirmations"
                      >
                        {sending ? "Sending…" : "Send my affirmations"}
                        <ArrowRight
                          size={17}
                          strokeWidth={1.75}
                          className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                      </PillButton>
                      <p className="mt-3 text-sm text-ink-soft">
                        We'll email your finished audio within 48 hours. Questions in the meantime?
                        Reach us at{" "}
                        <a
                          href="mailto:universaflow@outlook.com"
                          className="text-terracotta underline underline-offset-2"
                        >
                          universaflow@outlook.com
                        </a>
                      </p>
                    </div>
                  </form>
                </Reveal>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CreateMyAudio;
